/**
 * Custom hook for TanStack Table configuration
 * Manages column definitions, sorting, memoization, and pagination
 *
 * @module MetricDataDevTool/hooks/useTableConfig
 */

import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel
} from '@tanstack/react-table';
import { generateColumns } from '../services/tableDataService';
import { TABLE_CONFIG } from '../constants';

/**
 * Custom hook for TanStack Table configuration
 * @param {Array} data - Table data
 * @param {Object} aggregationConfig - Aggregation configuration
 * @param {number} [pageSize=25] - Initial page size
 * @returns {Object} Table instance and configuration
 */
export function useTableConfig(data, aggregationConfig, pageSize = TABLE_CONFIG.DEFAULT_ROWS_PER_PAGE) {
  // Memoize columns based on aggregation config
  const columns = useMemo(() => {
    return generateColumns(aggregationConfig);
  }, [aggregationConfig]);

  // Memoize table data
  const tableData = useMemo(() => {
    return data || [];
  }, [data]);

  // Create table instance
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize
      }
    }
  });

  return {
    table,
    columns,
    data: tableData
  };
}
